import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Login
        </Link>

        {sent ? (
          <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-[#56dfb4] mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Check Your Inbox</h1>
            <p className="text-slate-500 mb-4">
              If an account exists for <strong>{email}</strong>, you'll receive a password reset link. If you have SMS alerts enabled, the link will also be sent to your phone.
            </p>
            <p className="text-sm text-slate-400">The link expires in 1 hour.</p>
            <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-100 text-left" data-testid="forgot-password-help-banner">
              <p className="text-sm font-medium text-slate-700 mb-1">Didn&apos;t get the email?</p>
              <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
                <li>Check your <strong>Spam</strong>, <strong>Junk</strong>, or <strong>Promotions</strong> folder.</li>
                <li>Make sure the email you entered matches the one on your account (typos happen!).</li>
                <li>Still stuck? Email us at <a href="mailto:meetformahjong@gmail.com" className="text-[#56c6df] hover:underline font-medium">meetformahjong@gmail.com</a> and we&apos;ll reset it for you.</li>
              </ul>
            </div>
            <Link to="/login" className="block mt-6">
              <Button variant="outline" className="rounded-full px-6">Return to Login</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Forgot Password</h1>
              <p className="text-slate-500 mt-2">Enter your email and we'll send you a reset link</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-5">
              <div>
                <Label className="text-sm mb-1.5 block">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="rounded-xl pl-10"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] py-3 font-medium" data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
